import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Col, Row } from "reactstrap"
import { Waypoint } from "react-waypoint"
import Trip from "components/trips/Trip"
import Spinner from "components/common/Spinner"
import ZoomButtons from "components/map/ZoomButtons"
import CurrentLocationButton from "components/map/CurrentLocationButton"
import TripMapMarker from "components/trips/TripMapMarker"
import TripMapCluster from "components/trips/TripMapCluster"
import { groupTripsByLocation } from "components/trips/utils"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import Loading from "modules/loader-watchers/Loading"
import { loadingSelector } from "modules/loader-watchers/selectors"
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api"
import { pick, debounce } from "lodash"
import { formatAddressComponent, currentLocation, updateLocalStorageBrowserLocation } from "helpers/location"
import { isEmptyFields } from "helpers/form"
import { isLastPage } from "helpers/pagination"
import { useTitle } from "hooks/useTitle"
import useGeocoder from "modules/google/hooks/useGeocoder"
import useSearchFilter from "hooks/useSearchFilter"
import { Alert } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { cleanTrip, cleanTrips, getTrips } from "store/trips"
import { collectionAnySelector, collectionSelector, metadataSelector } from "store/selectors"
import { API_OPTIONS, DEFAULT_ZOOM, MAP_OPTIONS, MIN_ZOOM, WORLD_RESTRICTION, DEFAULT_MAP_VIEW } from "constants/map"
import { TRIP_FILTERS, TRIP_FILTERS_HELP_ATTRIBUTES } from "constants/trip"
import { OTHER_FILTERS } from "components/filters/constants"
import { cleanedExperiences } from "store/experiences"
import EmptyState from "components/common/EmptyState"
import SortDropdownAntd from "components/trips/filters/TripsSort"
import { useDevice } from "hooks/useDevice"
import styles from "./style.module.css"

export default function Trips() {
  useTitle("Search Trips")
  const { t } = useTranslation()
  const { isLoaded } = useJsApiLoader(API_OPTIONS)
  const dispatch = useDispatch()
  const geocoder = useGeocoder(null)
  const headerHeight = useSelector((state) => state.window.headerHeight)
  const trips = useSelector(collectionSelector("trips"))
  const hasTrips = useSelector(collectionAnySelector("trips"))
  const metadata = useSelector(metadataSelector("trips"))
  const isFetching = useSelector(loadingSelector("trips"))
  const [hoverItemId, setHoverItemId] = useState(null)
  const [activeItemId, setActiveItemId] = useState(null)
  const view = useSelector((state) => state.trips.view)
  const { isMobile } = useDevice()
  // On mobile the split view collapses into a full-screen map with a bottom
  // card carousel instead of a stacked, separately-scrolling list.
  const isMobileMap = isMobile && view === "map"
  const [sort, setSort] = useState({ price_sort: "", distance_sort: "" })
  const tripMouseOver = useRef(false)
  const [map, setMap] = useState(null)
  const [boundsLatLng, setBoundsLatLng] = useState(null)
  const lastFetchedBounds = useRef(null)
  const [params, updateSearchFilters, searchUpdatedCallback] = useSearchFilter({
    accessAttributes: TRIP_FILTERS,
    exceptDependencies: TRIP_FILTERS_HELP_ATTRIBUTES,
    additionalDependencies: [boundsLatLng?.northeast_corner, boundsLatLng?.southwest_corner]
  })

  const priceRange = useSelector((state) => state.trips.priceRange)
  const language = useSelector((state) => state.trips.language)
  const skillLevel = useSelector((state) => state.trips.skillLevel)
  const experiences = useSelector((state) => state.trips.experiences)
  const subcategories = useSelector((state) => state.trips.subcategories)
  const other = useSelector((state) => state.trips.other)
  const tripsSort = useSelector((state) => state.trips.sort)
  const guests = useSelector((state) => state.trips.guests)
  const calendar = useSelector((state) => state.trips.calendar)
  const paramsLatLng = useMemo(() => (isEmptyFields(params, ["lat", "lng"]) ? null : pick(params, ["lat", "lng"])), [params])

  const paramsBounds = useMemo(() => {
    const { northeast_corner, southwest_corner } = params
    if (!northeast_corner || !southwest_corner) return null
    const [neLat, neLng] = String(northeast_corner)
      .split(",")
      .map((n) => parseFloat(n))
    const [swLat, swLng] = String(southwest_corner)
      .split(",")
      .map((n) => parseFloat(n))
    if ([neLat, neLng, swLat, swLng].some((n) => !Number.isFinite(n))) return null
    return { ne: { lat: neLat, lng: neLng }, sw: { lat: swLat, lng: swLng } }
  }, [params])

  const [centerLatLng, setCenterLatLng] = useState(paramsLatLng || DEFAULT_MAP_VIEW)
  const zoom = map?.zoom || params.zoom || (paramsLatLng ? DEFAULT_ZOOM : DEFAULT_MAP_VIEW.zoom)
  const isProgrammaticMove = useRef(true)
  const hasFitToTrips = useRef(false)
  // True only after the user manually pans/zooms the map ("search this area").
  // Until then a location search fetches by radius/nearest like list view — so
  // the backend's "farther than 50 miles" fallback still works — instead of
  // being constrained to the viewport corners (which returns 0 when the nearest
  // experiences are outside the visible box).
  const userMovedMap = useRef(false)

  const tripGroups = useMemo(() => groupTripsByLocation(trips), [trips])

  const updateSearchHandler = useCallback(
    (location) =>
      geocoder({ location }, (place) =>
        updateSearchFilters({
          ...location,
          zoom,
          address: formatAddressComponent(place.address_components, ["locality", "political"]),
          northeast_corner: "",
          southwest_corner: ""
        })
      ),
    [zoom, geocoder, updateSearchFilters]
  )

  const debouncedSetBounds = useRef(debounce(setBoundsLatLng, 400)).current
  useEffect(() => () => debouncedSetBounds.cancel(), [debouncedSetBounds])

  const setBoundsHandler = useCallback(
    (loadedMap) => {
      const bounds = (loadedMap || map)?.getBounds()
      if (!bounds) return
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      const northeast_corner = Object.keys(ne)
        .reduce((acc, key) => acc.concat(ne[key]()), [])
        .join(", ")
      const southwest_corner = Object.keys(sw)
        .reduce((acc, key) => acc.concat(sw[key]()), [])
        .join(", ")
      debouncedSetBounds({ northeast_corner, southwest_corner })
    },
    [map, debouncedSetBounds]
  )

  const onLoad = useCallback((map) => setMap(map), [])
  const onUnmount = useCallback(() => setMap(null), [])

  const onIdleHandler = useCallback(() => {
    const mapCenter = map?.center
    if (!mapCenter) return

    const lat = mapCenter.lat()
    const lng = mapCenter.lng()

    if (isProgrammaticMove.current) {
      isProgrammaticMove.current = false
      setBoundsHandler(map)
      return
    }

    // Genuine user pan/zoom → switch to viewport-constrained "search this area".
    userMovedMap.current = true
    if (view === "map") updateSearchHandler({ lat, lng })
    setBoundsHandler(map)
  }, [map, view, updateSearchHandler, setBoundsHandler])
  useEffect(() => {
    if (!paramsBounds || !map || !window.google?.maps) return
    isProgrammaticMove.current = true
    userMovedMap.current = false
    hasFitToTrips.current = false
    const point = paramsLatLng || {
      lat: (paramsBounds.ne.lat + paramsBounds.sw.lat) / 2,
      lng: (paramsBounds.ne.lng + paramsBounds.sw.lng) / 2
    }
    map.fitBounds(new window.google.maps.LatLngBounds(paramsBounds.sw, paramsBounds.ne))
    const listener = window.google.maps.event.addListenerOnce(map, "idle", () => {
      if ((map.getZoom() || 0) > DEFAULT_ZOOM) {
        map.setZoom(DEFAULT_ZOOM)
        map.setCenter(point)
      }
    })
    setCenterLatLng(point)
    return () => listener.remove()
  }, [paramsBounds?.ne?.lat, paramsBounds?.ne?.lng, paramsBounds?.sw?.lat, paramsBounds?.sw?.lng, map]) //eslint-disable-line

  const lastCenteredAddress = useRef(null)
  useEffect(() => {
    if (paramsBounds || !paramsLatLng) return
    if (params.address && params.address === lastCenteredAddress.current) return
    lastCenteredAddress.current = params.address
    isProgrammaticMove.current = true
    userMovedMap.current = false
    hasFitToTrips.current = false
    setCenterLatLng(paramsLatLng)
  }, [params.address, paramsBounds, paramsLatLng]) //eslint-disable-line

  const hadSearchedLocation = useRef(!!paramsLatLng)
  useEffect(() => {
    const wasSearched = hadSearchedLocation.current
    hadSearchedLocation.current = !!paramsLatLng
    if (!wasSearched || paramsLatLng || paramsBounds || !map || !window.google?.maps) return
    isProgrammaticMove.current = true
    lastCenteredAddress.current = null
    setCenterLatLng(DEFAULT_MAP_VIEW)
    map.setCenter(DEFAULT_MAP_VIEW)
    map.setZoom(DEFAULT_MAP_VIEW.zoom)
  }, [paramsLatLng, paramsBounds, map]) //eslint-disable-line

  const locationStateRef = useRef({})
  locationStateRef.current = { hasLocation: !!(paramsLatLng || paramsBounds || params.address) }

  const [geoPermission, setGeoPermission] = useState(null)
  useEffect(() => {
    if (!navigator.permissions?.query) {
      setGeoPermission("granted")
      return
    }
    let status
    let cancelled = false
    const update = () => !cancelled && setGeoPermission(status.state)
    navigator.permissions
      .query({ name: "geolocation" })
      .then((result) => {
        if (cancelled) return
        status = result
        setGeoPermission(result.state)
        result.addEventListener("change", update)
      })
      .catch(() => !cancelled && setGeoPermission("granted"))
    return () => {
      cancelled = true
      status?.removeEventListener("change", update)
    }
  }, [])

  useEffect(() => {
    if (!isLoaded || !map || !geoPermission) return
    const { hasLocation } = locationStateRef.current

    // An explicit location (a shared URL's params or a manual search) is always
    // honored, including when geolocation is denied — otherwise pasting a link
    // like /trips?lat=..&lng=..&address=.. into a browser that has blocked
    // location would wipe the params and snap back to the default world view.
    // Only auto-locate the user when permission allows it AND no location exists.
    if (hasLocation || geoPermission === "denied") return

    currentLocation((latLng) => {
      if (!latLng || latLng.default) return
      const location = { lat: latLng.lat, lng: latLng.lng }
      updateLocalStorageBrowserLocation(location)
      geocoder({ location }, (place) => {
        if (!place) return
        isProgrammaticMove.current = true
        setCenterLatLng(location)
        map.setCenter(location)
        map.setZoom(DEFAULT_ZOOM)
        updateSearchFilters({
          ...location,
          zoom: DEFAULT_ZOOM,
          address: formatAddressComponent(place.address_components, ["locality", "political"]),
          northeast_corner: "",
          southwest_corner: ""
        })
      })
    })
  }, [geoPermission, isLoaded, map]) //eslint-disable-line

  useEffect(() => {
    if (view !== "map" || !map || !window.google?.maps) return
    window.google.maps.event.trigger(map, "resize")
    if (centerLatLng) map.setCenter(centerLatLng)
  }, [view, map]) //eslint-disable-line

  useEffect(() => {
    if (hasFitToTrips.current || view !== "map" || !map || !window.google?.maps) return
    const points = trips.filter((trip) => trip.latitude != null && trip.longitude != null)
    if (!points.length) return
    hasFitToTrips.current = true
    isProgrammaticMove.current = true
    const bounds = new window.google.maps.LatLngBounds()
    points.forEach((trip) => bounds.extend(new window.google.maps.LatLng(trip.latitude, trip.longitude)))
    map.fitBounds(bounds)
    const listener = window.google.maps.event.addListenerOnce(map, "idle", () => {
      if ((map.getZoom() || 0) > DEFAULT_ZOOM) map.setZoom(DEFAULT_ZOOM)
    })
    return () => listener.remove()
  }, [trips, view, map]) //eslint-disable-line

  useEffect(() => {
    dispatch(cleanTrip())
    dispatch(cleanedExperiences())
    return () => {
      setMap(null)
      dispatch(cleanTrips())
    }
  }, []) //eslint-disable-line

  const fetchTrips = (nextPage = null) => {
    if (!boundsLatLng?.northeast_corner || !boundsLatLng?.southwest_corner) return

    if (isProgrammaticMove.current) {
      return
    }

    const payload = {
      // Map bounds constrain results to the visible viewport. Send them only in
      // map view AND only once the user has panned/zoomed ("search this area").
      // On a fresh location search we omit them so the backend applies its
      // radius + "farther than 50 miles" fallback, exactly like list view —
      // otherwise the map returns 0 when the nearest experiences sit outside the
      // viewport box. List view never sends them.
      ...(view === "map" &&
        userMovedMap.current && {
          northeast_corner: boundsLatLng.northeast_corner,
          southwest_corner: boundsLatLng.southwest_corner
        }),
      guest_count: guests.adults + guests.children === 0 ? "" : guests.adults + guests.children,
      from_date: calendar.from_date,
      to_date: calendar.to_date,
      priceRange: priceRange,
      skill_levels: skillLevel,
      ...(other.includes(OTHER_FILTERS.ALCOHOL_ALLOWED) && { alcohol_allowed: true }),
      ...(other.includes(OTHER_FILTERS.ACCESSIBLE) && { accessibility_enabled: true }),
      ...(other.includes(OTHER_FILTERS.CHILD_FRIENDLY) && { children_allowed: true }),
      category_ids: experiences,
      subcategory_ids: subcategories,
      price_sort: tripsSort.price_sort,
      ...(params.lat && params.lng && { distance_sort: tripsSort.distance_sort }),
      longitude: params.lng,
      latitude: params.lat,
      languages: language,
      per_page: 50,
      page: nextPage ? Number(metadata?.page || 1) + 1 : 1
    }

    const fetchKey = `${view}_${boundsLatLng.northeast_corner}_${boundsLatLng.southwest_corner}_${JSON.stringify({
      priceRange,
      skillLevel,
      other,
      experiences,
      subcategories,
      language,
      price_sort: tripsSort.price_sort,
      distance_sort: tripsSort.distance_sort,
      guest_count: guests.adults + guests.children,
      from_date: calendar.from_date,
      to_date: calendar.to_date
    })}`

    if (!nextPage && lastFetchedBounds.current === fetchKey) return

    if (nextPage && isFetching) return

    if (!nextPage) {
      window.scrollTo(0, 0)
      lastFetchedBounds.current = fetchKey
    }
    dispatch(getTrips(nextPage, payload))
  }

  searchUpdatedCallback(() => {
    fetchTrips()
  })

  const handleClearFilters = () => {
    updateSearchFilters({})
  }

  useEffect(() => {
    if (!isProgrammaticMove.current) {
      fetchTrips()
    }
    // eslint-disable-next-line
  }, [view, priceRange, language, skillLevel, experiences, subcategories, other, tripsSort.price_sort, tripsSort.distance_sort])

  return (
    <div className="vstack flex-fill bg-light z-0">
      <Row className="flex-fill g-0 z-0">
        <Col
          xs={12}
          md={7}
          className={`vstack z-0 order-md-2 ${isMobileMap ? "" : "sticky-top"} ${view === "map" ? "" : styles.mapHidden}`}
          style={isMobileMap ? { height: "45vh" } : { top: headerHeight, height: `calc(100vh - ${headerHeight}px)` }}
        >
          {isLoaded ? (
            <GoogleMap
              mapContainerClassName="h-100"
              options={{ ...MAP_OPTIONS, clickableIcons: false, minZoom: MIN_ZOOM, restriction: WORLD_RESTRICTION }}
              center={centerLatLng || paramsLatLng || DEFAULT_MAP_VIEW}
              zoom={zoom}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onIdle={onIdleHandler}
              onClick={() => !tripMouseOver.current && setActiveItemId(null)}
            >
              <div className="vstack position-absolute p-10 w-100 h-100 gap-10 pointer-events-none">
                <div className="pointer-events-auto hstack gap-2 ms-auto ">
                  <CurrentLocationButton />
                </div>
                {centerLatLng ? (
                  <>
                    {tripGroups.map((group) =>
                      group.length > 1 ? (
                        <TripMapCluster
                          key={group[0].id}
                          trips={group}
                          hoverItemId={hoverItemId}
                          activeItemId={activeItemId}
                          toggle={setActiveItemId}
                        />
                      ) : (
                        <TripMapMarker trip={group[0]} key={group[0].id} isActive={hoverItemId === group[0].id} toggle={setActiveItemId} />
                      )
                    )}
                    {activeItemId && (
                      <TripMapMarker
                        key={activeItemId}
                        trip={trips.find((trip) => trip.id === activeItemId)}
                        isActive
                        withPreview
                        toggle={setActiveItemId}
                        onMouseOver={() => (tripMouseOver.current = true)}
                        onMouseLeave={() => (tripMouseOver.current = false)}
                      />
                    )}
                  </>
                ) : (
                  <div className="hstack h-100 p-50">
                    <Spinner className="m-auto" />
                  </div>
                )}
                <ZoomButtons />
              </div>
            </GoogleMap>
          ) : (
            <div className="hstack h-100 p-50">
              <Spinner className="m-auto" />
            </div>
          )}
        </Col>
        <Col
          xs={12}
          md={view === "map" ? 5 : 12}
          className={`z-1 order-md-1 ${view === "map" && !isMobileMap ? "sticky-top overflow-auto" : ""}`}
          style={view === "map" && !isMobileMap ? { top: headerHeight, height: `calc(100vh - ${headerHeight}px)` } : undefined}
        >
          <FirstLoading name="trips" className="p-20 h-100">
            <Loading
              name="trips"
              className={`vstack p-20 bg-white shadow gap-2 ${view === "map" && !isMobileMap ? "h-100 overflow-auto" : ""}`}
              spinnerProps={{ position: "fixed" }}
            >
              {hasTrips ? (
                <>
                  <div className="mb-l h-auto">
                    <h4 className="fw-semibold">{t("filterTags.available_experiences")}</h4>
                    {metadata?.miles_away === true && (
                      <Alert
                        className={styles.alertInfo}
                        message={t("filterTags.no_matches_50_miles")}
                        type="info"
                        showIcon
                        closable
                        icon={<InfoCircleOutlined className={styles.infoIcon} />}
                      />
                    )}
                  </div>

                  <div className="hstack justify-between flex-wrap align-items-center gap-15 mb-xs">
                    <span>{t("filterTags.experiences_found", { count: metadata?.total_results })}</span>
                    <SortDropdownAntd value={sort} onChange={setSort} />
                  </div>
                  <Row className="g-15">
                    {trips.map((trip) => (
                      <Col xs={12} sm={view === "map" ? 12 : 6} lg={view === "map" ? 12 : 3} key={trip.id} className="d-flex">
                        <Trip
                          trip={trip}
                          className="w-100"
                          onMouseOver={() => setHoverItemId(trip.id)}
                          onMouseLeave={() => setHoverItemId(null)}
                          withRating
                          isVertical={view === "map" && !isMobileMap}
                        />
                      </Col>
                    ))}
                  </Row>
                  {!isLastPage(metadata) && !isFetching && <Waypoint onEnter={() => fetchTrips(true)} />}
                </>
              ) : (
                <EmptyState
                  title={t("filterTags.no_experiences_found")}
                  description={t("filterTags.no_matches_area")}
                  onClear={handleClearFilters}
                />
              )}
            </Loading>
          </FirstLoading>
        </Col>
      </Row>
    </div>
  )
}
